'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import PortalIdParser from 'PortalIdParser';
import H4 from 'UIComponents/elements/headings/H4';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import { THUNDERDOME } from 'HubStyleTokens/colors';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import GlobalTimeRangeFilterContainer from 'AdsUI/components/GlobalTimeRangeFilterContainer';
import GlobalCampaignStatusFilterContainer from 'AdsUI/components/GlobalCampaignStatusFilterContainer';
import GlobalAdAccountFilterContainer from 'AdsUI/components/GlobalAdAccountFilterContainer';
import GlobalAttributionViewFilterContainer from 'AdsUI/components/GlobalAttributionViewFilterContainer';
import GlobalUsersAndTeamsFilterContainer from 'AdsUI/components/GlobalUsersAndTeamsFilterContainer';
import CampaignsTableContainer from 'AdsUI/containers/campaigns/CampaignsTableContainer';
import AllCampaignsPerformanceWells from 'AdsUI/components/campaigns/AllCampaignsPerformanceWells';
import TimeZoneMessage from 'AdsUI/components/TimeZoneMessage';
import { NavMarker } from 'react-rhumb';
import makeLoadable from 'AdsUI/lib/makeLoadable';
import { EntityTypes } from 'AdsUI/lib/Constants';
import FilterBarWithExport from '../FilterBarWithExport';
import LIPurgedDataBanner from 'AdsUI/components/LIPurgedDataBanner';
import { hasAccessToRoiReporting } from 'AdsUI/selectors/AuthSelectors';
import { getSeenAccountLimitIncreaseAlert } from 'AdsUI/selectors/UserAttributesSelectors';
import { getRequestStatus } from 'AdsUI/selectors/RequestStatusSelectors';
import { saveUserAttribute } from 'AdsUI/actions/UserAttributesActions';
import { getHubSpotUserId } from 'AdsUI/selectors/AuthSelectors';
import { SEEN_ACCOUNT_LIMIT_INCREASE_ALERT } from 'AdsUI/data/UserAttributesData';
const IconContainer = styled.div.withConfig({
  displayName: "CampaignListPage__IconContainer",
  componentId: "sc-1t3ox5i-0"
})(["position:absolute;top:0;left:-1.5rem;"]);
const OnboardingWidgetContainerLoader = makeLoadable(() => import('AdsUI/containers/onboarding/OnboardingWidgetContainer'));
const settingsUrl = `/settings/${PortalIdParser.get()}/marketing/ads`;

const CampaignListPage = ({
  campaignList,
  campaignListRS,
  showOnboardingWidget,
  showLIBanner,
  hasAccessToUsersAndTeams
}) => {
  const dispatch = useDispatch();
  const seenAccountLimitIncreaseAlert = useSelector(getSeenAccountLimitIncreaseAlert);
  const userAttributesRequestStatus = useSelector(getRequestStatus('userAttributes'));
  const isFreeOrStarterTier = !useSelector(hasAccessToRoiReporting);
  const userId = useSelector(getHubSpotUserId);
  const showAccountLimitIncreaseAlert = useCallback(() => {
    return isFreeOrStarterTier && !seenAccountLimitIncreaseAlert && userAttributesRequestStatus.fetched;
  }, [isFreeOrStarterTier, seenAccountLimitIncreaseAlert, userAttributesRequestStatus.fetched]);
  const [openAccountLimitAlert, setOpenAccountLimitAlert] = useState(true);

  const closeAccountLimitAlert = () => {
    setOpenAccountLimitAlert(false);
    dispatch(saveUserAttribute(SEEN_ACCOUNT_LIMIT_INCREASE_ALERT, userId, true));
  };

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [showAccountLimitIncreaseAlert() && /*#__PURE__*/_jsx(IconContainer, {
      children: /*#__PURE__*/_jsx(UIPopover, {
        use: "shepherd",
        width: 350,
        open: openAccountLimitAlert,
        showCloseButton: true,
        onOpenChange: evt => !evt.target.value && closeAccountLimitAlert(),
        placement: "bottom",
        autoPlacement: false,
        content: {
          header: /*#__PURE__*/_jsx(H4, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "adAccountIncreaseAlert.title"
            })
          }),
          body: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "adAccountIncreaseAlert.message",
            options: {
              settingsUrl
            }
          })
        },
        children: /*#__PURE__*/_jsx(UIIconButton, {
          use: "transparent",
          "aria-pressed": openAccountLimitAlert,
          onClick: () => setOpenAccountLimitAlert(true),
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "info",
            size: "xs",
            color: THUNDERDOME
          })
        })
      })
    }), /*#__PURE__*/_jsxs(FilterBarWithExport, {
      entityType: EntityTypes.CAMPAIGN,
      loaded: campaignListRS.fetchedSuccessfully(),
      children: [hasAccessToUsersAndTeams && /*#__PURE__*/_jsx(GlobalUsersAndTeamsFilterContainer, {}), /*#__PURE__*/_jsx(GlobalAdAccountFilterContainer, {}), /*#__PURE__*/_jsx(GlobalTimeRangeFilterContainer, {}), /*#__PURE__*/_jsx(GlobalAttributionViewFilterContainer, {}), /*#__PURE__*/_jsx(GlobalCampaignStatusFilterContainer, {})]
    }), showLIBanner && /*#__PURE__*/_jsx(LIPurgedDataBanner, {}), showOnboardingWidget && /*#__PURE__*/_jsx(OnboardingWidgetContainerLoader, {}), /*#__PURE__*/_jsx(AllCampaignsPerformanceWells, {
      campaignData: campaignList,
      campaignDataRS: campaignListRS
    }), /*#__PURE__*/_jsx(CampaignsTableContainer, {}), /*#__PURE__*/_jsx(TimeZoneMessage, {}), /*#__PURE__*/_jsx(NavMarker, {
      name: "CAMPAIGN_LIST_PAGE"
    })]
  });
};

export default CampaignListPage;